import { useState, useEffect } from 'react';
import { trpc } from '../../lib/trpc.js';
import { getStoredUser } from '../../lib/auth.js';

const tabs = ['Buyers', 'Publishers'];

interface Buyer {
  id: string;
  name: string;
  company?: string;
  stage?: string;
  healthScore?: number;
  calls?: number;
  revenue?: number;
  churnRisk?: string;
}

interface Publisher {
  id: string;
  name: string;
  status?: string;
}

const stageColors: Record<string, string> = {
  prospecting:  'bg-slate-500/20 text-slate-400',
  trial:        'bg-blue-500/20 text-blue-400',
  negotiating:  'bg-amber-500/20 text-amber-400',
  active:       'bg-emerald-500/20 text-emerald-400',
  scaled:       'bg-purple-500/20 text-purple-400',
  at_risk:      'bg-rose-500/20 text-rose-400',
  churned:      'bg-slate-700/50 text-slate-500',
};

function HealthBar({ score }: { score: number }) {
  const color = score >= 80 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs text-slate-400">{score}</span>
    </div>
  );
}

function Skeleton({ cols }: { cols: number }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex gap-4 p-4 border-b border-slate-800/50">
          {[...Array(cols)].map((_, j) => (
            <div key={j} className="h-4 bg-slate-800 rounded flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function NetworkPage() {
  const [activeTab, setActiveTab] = useState('Buyers');
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [loadingBuyers, setLoadingBuyers] = useState(true);
  const [loadingPublishers, setLoadingPublishers] = useState(true);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoadingBuyers(false); setLoadingPublishers(false); return; }

    trpc.network.listBuyers.query({ tenantId: user.tenantId })
      .then(res => setBuyers((res.buyers ?? []) as Buyer[]))
      .catch(console.error)
      .finally(() => setLoadingBuyers(false));

    trpc.network.listPublishers.query({ tenantId: user.tenantId })
      .then(res => setPublishers((res.publishers ?? []) as Publisher[]))
      .catch(console.error)
      .finally(() => setLoadingPublishers(false));
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">👥 Network</h1>
        <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium">
          + Add Contact
        </button>
      </div>

      <div className="overflow-x-auto -mx-1">
        <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800 min-w-max mx-1">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'Buyers' && (
        loadingBuyers ? <Skeleton cols={7} /> : (
          buyers.length === 0 ? (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center">
              <div className="text-4xl mb-4">👥</div>
              <h3 className="text-lg font-semibold text-white mb-2">No buyers yet</h3>
              <p className="text-slate-400 mb-4">Add your first buyer to start routing calls.</p>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm">+ Add Buyer</button>
            </div>
          ) : (
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Buyer</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Company</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Stage</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Health</th>
                    <th className="text-right p-4 text-xs font-medium text-slate-400 uppercase">Calls</th>
                    <th className="text-right p-4 text-xs font-medium text-slate-400 uppercase">Revenue</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {buyers.map(b => (
                    <tr key={b.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer">
                      <td className="p-4 text-sm font-medium text-white">{b.name}</td>
                      <td className="p-4 text-sm text-slate-300">{b.company ?? '—'}</td>
                      <td className="p-4">
                        {b.stage ? (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${stageColors[b.stage] ?? 'bg-slate-700 text-slate-300'}`}>
                            {b.stage.replace('_', ' ')}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="p-4"><HealthBar score={b.healthScore ?? 0} /></td>
                      <td className="p-4 text-sm text-slate-300 text-right">{(b.calls ?? 0).toLocaleString()}</td>
                      <td className="p-4 text-sm text-emerald-400 text-right font-medium">${(b.revenue ?? 0).toLocaleString()}</td>
                      <td className="p-4">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          b.churnRisk === 'high' ? 'bg-rose-500/20 text-rose-400' :
                          b.churnRisk === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {b.churnRisk ?? 'low'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )
        )
      )}

      {activeTab === 'Publishers' && (
        loadingPublishers ? <Skeleton cols={3} /> : (
          publishers.length === 0 ? (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
              <div className="text-4xl mb-4">📡</div>
              <h3 className="text-lg font-semibold text-white mb-2">Publisher / Traffic Source Manager</h3>
              <p className="text-slate-400 mb-4">Manage traffic sources, quality scores, payout terms, and compliance</p>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm">Add Publisher</button>
            </div>
          ) : (
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Publisher</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {publishers.map(p => (
                    <tr key={p.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                      <td className="p-4 text-sm font-medium text-white">{p.name}</td>
                      <td className="p-4 text-sm text-slate-300">{p.status ?? 'active'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )
        )
      )}
    </div>
  );
}
